import { Dispatch, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch as ReduxDispatch } from 'redux';
import config from '../../../../app.config.json';
import axios from '../services/axios';

type Return<T> = [T | undefined, any | undefined, boolean, (url?: string) => void, Dispatch<T | undefined>];

type CachePolicy = 'no-cache' | 'cache-first';

let inMemoryCache: { [key: string]: any } = {};

export const clearApiCache = () => {
  inMemoryCache = {};
};

const fetchData = async (url: string, cachePolicy: CachePolicy, dispatch: ReduxDispatch) => {
  if (cachePolicy === 'cache-first' && inMemoryCache[url] !== undefined) {
    return inMemoryCache[url];
  }

  const result = await axios(
    {
      method: 'GET',
      url: `${config.apiUrl}${url}`,
    },
    dispatch,
  );

  inMemoryCache[url] = result;

  return result;
};

const useApi = <T = any>(url: string, loadOnMount: boolean = true, cachePolicy?: CachePolicy): Return<T> => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [data, setData] = useState<T>();
  const dispatch = useDispatch();

  const refetch = async (fetchUrl?: string) => {
    setLoading(true);

    try {
      const result = await fetchData(fetchUrl || url, cachePolicy || 'no-cache', dispatch);

      setData(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadOnMount) {
      refetch();
    }
  }, []);

  return [data, error, loading, refetch, setData];
};

export const clearCacheItem = (url: RegExp) => {
  const arrayOfKeysInCache = Object.keys(inMemoryCache);
  for (let i = 0; i < arrayOfKeysInCache.length; i += 1) {
    if (url.test(arrayOfKeysInCache[i])) {
      delete inMemoryCache[arrayOfKeysInCache[i]];
    }
  }
};

export default useApi;
